import * as React from "react"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { StyledShareIcon } from "./buttons-main-screen.styled"
import { brandingSelector } from "../../../recoil/atoms/room"
import { IconButton } from "../../common/buttons"
import { viewDependingLayoutAtom } from "../../../recoil/atoms/view"
import { ShareIconProps } from "./buttons-main-screen.interface"
import { ConsoleLogger } from "../../../util/logger"
import { shareLinksModalAtom } from "../../../recoil/atoms/layout/share-links-modal"
import { useLocalUserType } from "../../../hooks/useLocalUserType"
import { roomLinksSelector } from "../../../recoil/atoms/roomLinks"
import MOLocation from "../../../util/MOLocation"
import useMeetingClient from "../../../hooks/useMeetingClient"
import { addHitAreaStyle } from "../../../util/styleUtils"

const logger = new ConsoleLogger()

const _ShareIcon: React.FC<ShareIconProps> = ({
  isMobile,
  changeSizeIconDependingHeightVideo
}) => {
  const { isLandscape } = useRecoilValue(viewDependingLayoutAtom)
  const roomStyles = useRecoilValue(brandingSelector)
  const setShareLinksModal = useSetRecoilState(shareLinksModalAtom)
  const { isAudience } = useLocalUserType()
  const { audienceHash } = useRecoilValue(roomLinksSelector)
  const meetingClient = useMeetingClient()

  /** Get audience user room URL */
  const getAudienceRoomURL = React.useCallback(() => {
    const currentUrl = MOLocation.getLocation().origin
    return isAudience
      ? `${currentUrl}/${meetingClient.getRoomHash()}`
      : `${currentUrl}/${audienceHash}`
  }, [isAudience, meetingClient, audienceHash])

  // This method will share the audience link
  const handleShareLink = () => {
    const audienceURL = getAudienceRoomURL()
    meetingClient?.userEventLog.submitShareButtonClickEvent()
    logger.log("Link to share:", audienceHash)

    if (
      typeof window !== "undefined" &&
      window.navigator &&
      window.navigator.share
    ) {
      window.navigator
        .share({ url: audienceURL })
        .then(() => {
          logger.log("Link successfully shared")
        })
        .catch(error => {
          logger.error("Error when trying to share a link:", { error })
          if (error.name === "NotAllowedError") {
            // NotAllowedError is thrown when page is render inside iframe, then show share modal like desktop
            setShareLinksModal(true)
          }
        })
    } else {

      const wasSuccessfullyOpen =
        meetingClient.nativeBridge.eventShareButtonPressed({
          shareLink: audienceURL
        })
      
      if (!wasSuccessfullyOpen) { 
        // fallback - show share modal
        setShareLinksModal(true)
      }
    }
  }

  const openShareLinksModal = ( event )=> {
    meetingClient?.userEventLog.submitShareButtonClickEvent()
    setShareLinksModal(true)
    event.stopPropagation()
    event.preventDefault()
  }

  return (
    <>
      {isMobile ? (
        <IconButton
          width="1.9rem"
          height={
            isLandscape
              ? "1.8rem"
              : changeSizeIconDependingHeightVideo &&
                `${changeSizeIconDependingHeightVideo(30, 40)}px`
          }
          isMobile={isMobile}
          onClick={handleShareLink}
          {...addHitAreaStyle({left: 5, right: 5, top: 0, bottom: 0})}
        >
          <StyledShareIcon
            roomStyles={roomStyles}
            size="1.9em"
            width="1.9rem"
            issmall={"true"}
            className="icon-ios"
            style={{ transform: "translate(0%, 0%)", fontSize: "0.95rem" }}
          />
        </IconButton>
      ) : (
        <StyledShareIcon
          roomStyles={roomStyles}
          size="1.9em"
          width="1.9rem"
          issmall={"true"}
          className="icon-ios"
          onClick={openShareLinksModal}
          style={{
            transform: "translate(0%, 0%)",
            fontSize: "0.95rem",
            marginBottom: "11px"
          }}
        />
      )}
    </>
  )
}

export const ShareIcon = React.memo(_ShareIcon)
